import React, { useState, useEffect } from "react";
import Button from "../../../components/common/Button";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  MdArrowBack,
  MdEdit,
  MdDelete,
  MdFitnessCenter,
  MdPlayArrow,
  MdHome,
  MdWork,
} from "react-icons/md";
import Card from "components/card";
import {
  useExercise,
  useExerciseOperations,
} from "../../../hooks/useExercises";
import { ExerciseService } from "../../../services/exerciseService";
import {
  UpdateExerciseInput,
  MuscleGroup,
  MuscleGroupAssignment,
} from "../../../types/exercise";
import {
  extractYouTubeVideoId,
  getYouTubeThumbnailFromUrl,
} from "../../../utils/youtube";
import ExerciseModal from "../../../components/exercises/ExerciseModal";
import DeleteConfirmationModal from "../../../components/modals/DeleteConfirmationModal";
import MuscleGroupManagementModal from "../../../components/exercises/MuscleGroupManagementModal";
import TrainingMethodBadge from "../../../components/badges/TrainingMethodBadge";
import LoadingSpinner from "components/common/LoadingSpinner";
import { logger } from "../../../utils/logger";

const ExerciseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const { exercise, loading, error, refresh } = useExercise(
    id ? parseInt(id) : null
  );
  const {
    updateExercise,
    deleteExercise: deleteExerciseOperation,
    loading: operationLoading,
    error: operationError,
  } = useExerciseOperations();

  // Exercise state
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Muscle groups state
  const [allMuscleGroups, setAllMuscleGroups] = useState<MuscleGroup[]>([]);
  const [muscleGroupsLoading, setMuscleGroupsLoading] = useState(false);
  const [showMuscleGroupModal, setShowMuscleGroupModal] = useState(false);

  // Fetch muscle groups on a mount
  useEffect(() => {
    const fetchMuscleGroups = async () => {
      setMuscleGroupsLoading(true);
      try {
        const muscleGroups = await ExerciseService.getAllMuscleGroups();
        setAllMuscleGroups(muscleGroups);
      } catch (error) {
        logger.error("Error fetching muscle groups:", error);
      } finally {
        setMuscleGroupsLoading(false);
      }
    };

    fetchMuscleGroups();
  }, []);

  const handleBack = () => {
    const returnPath =
      "/admin/exercise-library" + ((location.state as any)?.from || "");
    navigate(returnPath);
  };

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDeleteExercise = async () => {
    if (!exercise) return;

    const success = await deleteExerciseOperation(exercise.id);
    if (success) {
      const returnPath =
        "/admin/exercise-library" + ((location.state as any)?.from || "");
      navigate(returnPath);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleUpdateSubmit = async (exerciseData: any) => {
    if (!exercise) return;

    const updateData: UpdateExerciseInput = {
      name: exerciseData.name,
      is_at_home: exerciseData.is_at_home,
      is_gym_based: exerciseData.is_gym_based,
      training_method: exerciseData.training_method,
      type: exerciseData.type,
      yt_link: exerciseData.yt_link,
      target_muscle_group: exerciseData.target_muscle_group,
      description: exerciseData.description,
      admin_note: exerciseData.admin_note,
    };

    const updatedExercise = await updateExercise(exercise.id, updateData);
    if (updatedExercise) {
      refresh();
      setShowEditModal(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
  };

  const handleManageMuscleGroups = () => {
    setShowMuscleGroupModal(true);
  };

  const handleSaveMuscleGroups = async (
    muscleGroups: MuscleGroupAssignment[]
  ) => {
    if (!exercise) return;

    const updateData: UpdateExerciseInput = {
      muscle_groups: muscleGroups,
    };

    const updatedExercise = await updateExercise(exercise.id, updateData);
    if (updatedExercise) {
      refresh();
      setShowMuscleGroupModal(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !exercise) {
    return (
      <Card extra="w-full p-3 md:p-6">
        <div className="py-12 text-center">
          <div className="mb-4 text-lg text-red-500">
            {error || "Exercise not found"}
          </div>
          <button
            onClick={handleBack}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            <MdArrowBack className="h-6 w-6 md:mr-2 md:h-4 md:w-4" />
            <span className="hidden md:inline">Back to Exercise Library</span>
          </button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Back Button and Actions */}
      <div className="flex items-end justify-between">
        <button
          onClick={handleBack}
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
        >
          <MdArrowBack className="h-6 w-6 md:mr-2 md:h-4 md:w-4" />
          <span className="hidden md:inline">Back to Exercise Library</span>
        </button>

        <div className="mt-3 flex flex-wrap justify-end gap-3">
          <Button onClick={handleEdit} disabled={operationLoading}>
            <MdEdit className="mr-2 h-4 w-4" />
            Edit Exercise
          </Button>
          <button
            onClick={handleDelete}
            disabled={operationLoading}
            className="inline-flex items-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 disabled:opacity-50"
          >
            <MdDelete className="mr-2 h-4 w-4" />
            Delete Exercise
          </button>
        </div>
      </div>

      {/* Exercise Profile Card */}
      <Card extra="w-full p-3 md:p-6">
        <div className="flex items-start space-x-6">
          {/* Exercise Thumbnail or Icon */}
          <div className="flex-shrink-0">
            {exercise.yt_link ? (
              <>
                <img
                  src={
                    getYouTubeThumbnailFromUrl(exercise.yt_link, "hqdefault") ||
                    ""
                  }
                  alt={`${exercise.name} thumbnail`}
                  className="h-24 w-32 rounded-lg object-cover"
                  onError={(e) => {
                    // Fallback to placeholder if the thumbnail fails to load
                    const target = e.target as HTMLImageElement;
                    target.style.display = "none";
                    target.nextElementSibling?.classList.remove("hidden");
                  }}
                />
              </>
            ) : (
              <div className="flex h-24 w-32 items-center justify-center rounded-lg bg-biafit-primary text-3xl font-bold text-white">
                <MdFitnessCenter className="h-12 w-12" />
              </div>
            )}
          </div>

          {/* Exercise Info */}
          <div className="min-w-0 flex-1">
            <div className="mb-4 flex items-center space-x-3">
              <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
                {exercise.name}
              </h1>
            </div>

            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">
                  Description
                </label>
                <div className="text-navy-700 dark:text-white">
                  {exercise.description ? (
                    <p className="whitespace-pre-wrap">
                      {exercise.description}
                    </p>
                  ) : (
                    <p className="italic text-gray-400">
                      No description provided
                    </p>
                  )}
                </div>
              </div>

              {exercise.admin_note && (
                <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-navy-700 dark:border-green-800 dark:bg-green-900/20 dark:text-white">
                  <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Admin Notes
                  </label>
                  <p className="whitespace-pre-wrap">{exercise.admin_note}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Exercise Information Card */}
      <Card extra="w-full p-3 md:p-6">
        <h2 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
          Exercise Information
        </h2>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">
              Training Method
            </label>
            <TrainingMethodBadge
              trainingMethod={exercise.training_method}
              className="px-2.5"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">
              Exercise Type
            </label>
            {exercise.type && exercise.type.trim() ? (
              <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800 dark:bg-purple-900 dark:text-purple-300">
                {exercise.type}
              </span>
            ) : (
              <p className="italic text-gray-400">Not specified</p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">
              Exercise Location
            </label>
            <div className="flex flex-wrap gap-2">
              {exercise.is_at_home && (
                <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-300">
                  <MdHome className="mr-1 h-3 w-3" />
                  Can be done at home
                </span>
              )}
              {exercise.is_gym_based && (
                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                  <MdWork className="mr-1 h-3 w-3" />
                  Requires gym equipment
                </span>
              )}
              {!exercise.is_at_home && !exercise.is_gym_based && (
                <p className="italic text-gray-400">Not specified</p>
              )}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">
              Video Tutorial
            </label>
            {exercise.yt_link ? (
              <a
                href={exercise.yt_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-biafit-primary hover:text-biafit-secondary"
              >
                <MdPlayArrow className="mr-1 h-4 w-4" />
                Watch on YouTube
              </a>
            ) : (
              <p className="italic text-gray-400">No video available</p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">
              Created Date
            </label>
            <p className="text-navy-700 dark:text-white">
              {new Date(exercise.created_at).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>

          {exercise.yt_link && (
            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">
                YouTube URL
              </label>
              <p className="break-all font-mono text-sm text-navy-700 dark:text-white">
                {exercise.yt_link}
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Video Embed Card */}
      {exercise.yt_link && (
        <Card extra="w-full p-3 md:p-6">
          <h2 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
            Exercise Demonstration
          </h2>

          <div className="aspect-w-16 aspect-h-9">
            <iframe
              src={`https://www.youtube.com/embed/${
                extractYouTubeVideoId(exercise.yt_link) || ""
              }`}
              title={`${exercise.name} - Exercise Demonstration`}
              className="h-64 w-full rounded-lg md:h-96"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </Card>
      )}

      {/* Muscle Groups Card */}
      <Card extra="w-full p-3 md:p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex flex-col items-start gap-1 md:flex-row md:items-center md:gap-2">
            <h2 className="text-xl font-bold text-navy-700 dark:text-white">
              Muscle Groups
            </h2>
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800 dark:bg-gray-800 dark:text-gray-200">
              {(exercise.primary_muscle_groups?.length || 0) +
                (exercise.secondary_muscle_groups?.length || 0)}{" "}
              assigned
            </span>
          </div>
          <button
            onClick={handleManageMuscleGroups}
            disabled={operationLoading || muscleGroupsLoading}
            className="inline-flex items-center rounded-md bg-biafit-primary px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-biafit-secondary disabled:opacity-50"
          >
            <MdFitnessCenter className="mr-2 h-4 w-4" />
            Manage Muscle Groups
          </button>
        </div>

        {muscleGroupsLoading ? (
          <LoadingSpinner size="lg" />
        ) : (
          <div className="space-y-4">
            {/* Primary Muscle Groups */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-500 dark:text-gray-400">
                Primary Muscle Groups
              </label>
              {exercise.primary_muscle_groups &&
              exercise.primary_muscle_groups.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {exercise.primary_muscle_groups.map((mg) => (
                    <span
                      key={mg.id}
                      className="inline-flex items-center rounded-full bg-biafit-primary px-3 py-1 text-sm font-medium text-white"
                    >
                      {mg.name}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm italic text-gray-400">
                  No primary muscle groups assigned
                </p>
              )}
            </div>

            {/* Secondary Muscle Groups */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-500 dark:text-gray-400">
                Secondary Muscle Groups
              </label>
              {exercise.secondary_muscle_groups &&
              exercise.secondary_muscle_groups.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {exercise.secondary_muscle_groups.map((mg) => (
                    <span
                      key={mg.id}
                      className="inline-flex items-center rounded-full bg-purple-100 px-3 py-1 text-sm font-medium text-purple-800 dark:bg-purple-900 dark:text-purple-300"
                    >
                      {mg.name}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm italic text-gray-400">
                  No secondary muscle groups assigned
                </p>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Edit Exercise Modal */}
      <ExerciseModal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        onSubmit={handleUpdateSubmit}
        exercise={exercise}
        mode="edit"
        loading={operationLoading}
        error={operationError}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={confirmDeleteExercise}
        title="Delete Exercise"
        message="Are you sure you want to delete this exercise?"
        itemName={exercise?.name || "Untitled Exercise"}
        loading={operationLoading}
        error={operationError}
      />

      {/* Muscle Group Management Modal */}
      <MuscleGroupManagementModal
        isOpen={showMuscleGroupModal}
        onClose={() => setShowMuscleGroupModal(false)}
        onSave={handleSaveMuscleGroups}
        availableMuscleGroups={allMuscleGroups}
        currentPrimary={exercise.primary_muscle_groups}
        currentSecondary={exercise.secondary_muscle_groups}
        loading={operationLoading}
        error={operationError}
      />
    </div>
  );
};

export default ExerciseDetail;
